/**
 * StarredMessagesPanel — Collapsible panel showing starred messages (per-chat)
 * Used in ChannelInfoPage. Tapping a message navigates to it in chat.
 */

import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { useStarredMessages } from '../hooks';
import { ChatAvatar } from './ChatAvatar';

interface StarredMsg {
  id: string;
  conversationId: string;
  sender: { avatar?: string; name: string };
  createdAt: string;
  content?: string | null;
  type: string;
}

interface StarredMessagesPanelProps {
  conversationId: string;
  onMessageTap?: (messageId: string) => void;
}

export const StarredMessagesPanel = memo(function StarredMessagesPanel({
  conversationId,
  onMessageTap,
}: StarredMessagesPanelProps) {
  const { t, i18n } = useTranslation();
  const { data: starredMessages } = useStarredMessages(conversationId);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!starredMessages || starredMessages.length === 0) return null;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const isHe = i18n.language === 'he';
    return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="mx-4 mb-3">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between rounded-xl border border-white/20 bg-white/40 px-4 py-3 transition-all hover:bg-white/50 active:scale-[0.99]"
      >
        <div className="flex items-center gap-2">
          {/* Star icon */}
          <svg className="h-4 w-4 text-yellow-500" viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          <span className="text-sm font-medium text-neutral-700">
            {t('messaging.starredMessages')}
          </span>
          <span className="rounded-full bg-yellow-100 px-1.5 py-0.5 text-xs font-bold text-yellow-700">
            {starredMessages.length}
          </span>
        </div>
        <svg
          className={`h-4 w-4 text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-1 space-y-1">
              {starredMessages.map((msg: StarredMsg) => (
                <button
                  key={msg.id}
                  type="button"
                  onClick={() => onMessageTap?.(msg.id)}
                  className="w-full rounded-xl border border-white/20 bg-white/30 px-3 py-2 text-start transition-colors hover:bg-white/50 active:scale-[0.99]"
                >
                  <div className="mb-1 flex items-center gap-2">
                    <ChatAvatar src={msg.sender.avatar} name={msg.sender.name} size="h-5 w-5" />
                    <span className="text-xs font-medium text-neutral-700">{msg.sender.name}</span>
                    <span className="text-[10px] text-neutral-400">
                      {formatDate(msg.createdAt)}
                    </span>
                  </div>
                  <p className="line-clamp-2 text-sm text-neutral-600" dir="auto">
                    {msg.content || `[${msg.type}]`}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
